import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider, CssBaseline } from '@mui/material';
import { getCookie, setCookie } from '../common/cookies';
import { buildTheme } from '../common/theme/buildTheme';

export type ThemeMode = 'light' | 'dark' | 'system';

// Shared across demo.*/admin.* subdomains so theme choice stays in sync between the two apps.
const THEME_COOKIE = 'sentio_theme_mode';
// Pre-existing per-app key, kept only to migrate a user's prior preference into the shared cookie.
const LEGACY_STORAGE_KEY = 'theme_mode';

interface ThemeContextType {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  isDark: boolean;
}

const ThemeContext = createContext<ThemeContextType>({
  mode: 'system',
  setMode: () => {},
  isDark: false,
});

export const useThemeContext = () => useContext(ThemeContext);

function getSystemDark(): boolean {
  return typeof window !== 'undefined'
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : false;
}

export const AppThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [mode, setModeState] = useState<ThemeMode>(
    () =>
      (getCookie(THEME_COOKIE) as ThemeMode | null) ??
      (localStorage.getItem(LEGACY_STORAGE_KEY) as ThemeMode | null) ??
      'system'
  );
  const [systemDark, setSystemDark] = useState<boolean>(getSystemDark);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // One-time migration: establish the shared cookie from this app's prior local preference
  // so the other app converges to it on its next load.
  useEffect(() => {
    if (!getCookie(THEME_COOKIE)) {
      setCookie(THEME_COOKIE, mode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setMode = (m: ThemeMode) => {
    setCookie(THEME_COOKIE, m);
    setModeState(m);
  };

  const isDark = mode === 'dark' || (mode === 'system' && systemDark);

  const muiTheme = useMemo(() => buildTheme(isDark), [isDark]);

  return (
    <ThemeContext.Provider value={{ mode, setMode, isDark }}>
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
